import { WIRE_OFFSET_DATETIME_PATTERN } from "./wire-patterns";
import { z } from "zod";
import {
  communityAddressSchema,
  communityFunnelOutcomeSchema,
  communityFunnelStepSchema,
  communityVerifyRequestSchema,
  communityXProfileImageSchema,
} from "./community-v1";
import { communityAchievementsSchema } from "./community-progress-v1";
import {
  genesisInviteStatusSchema,
  genesisMemberCriteriaFailureSchema,
  genesisTierSchema,
  genesisTierSharesSchema,
} from "./community-genesis-v1";
import { codeCoverSchema } from "./community-code-v1";
import { holderAdminPersonBadgeSchema } from "./community-holders-v1";
import { memeSchema, memeMediaSchema, MEME_PAGE_SIZE } from "./community-memes-v1";
import { referralCodeSchema } from "./community-v1";

export const communityAdminMemeQuerySchema = z.object({
  status: z.enum(["live", "removed"]).default("live"),
  q: z.string().trim().min(1).max(100).optional(),
  creator: referralCodeSchema.optional(),
  cursor: z.string().min(1).max(2048).optional(),
}).strict();
export const communityAdminMemeSchema = memeSchema.extend({
  personId: z.string().uuid(),
  removedAt: z.stringFormat("date-time", WIRE_OFFSET_DATETIME_PATTERN).nullable(),
  removedBy: z.string().nullable(),
  removedReason: z.string().nullable(),
}).strict();
export const communityAdminMemesResponseSchema = z.object({
  memes: z.array(communityAdminMemeSchema).max(MEME_PAGE_SIZE),
  nextCursor: z.string().nullable(),
}).strict();
export const communityAdminMemeRemoveRequestSchema = z.object({
  reason: z.string().trim().min(3).max(500),
}).strict();
export const communityAdminMemeRemoveResponseSchema = z.object({
  ok: z.literal(true), purgeUrls: memeMediaSchema,
}).strict();
export const communityAdminMemeUploaderGrantSchema = z.object({
  code: referralCodeSchema, note: z.string().trim().max(200).optional(),
}).strict();
export const communityAdminMemeUploadersQuerySchema = z.object({
  state: z.enum(["active", "revoked"]).default("active"),
  cursor: z.string().min(1).max(2048).optional(),
}).strict();
export const communityAdminMemeUploaderSchema = z.object({
  personId: z.string().uuid(),
  /** As members see the creator: their chosen public alias when they hide X. */
  creator: memeSchema.shape.creator,
  /** The linked X account regardless of public display, for operators. */
  x: z.object({ username: z.string().min(1).max(64), name: z.string().max(128).nullable(), profileImageUrl: z.string().url().nullable() }).strict().nullable(),
  grantedAt: z.stringFormat("date-time", WIRE_OFFSET_DATETIME_PATTERN),
  grantedBy: z.string(),
  note: z.string().max(200).nullable(),
  revokedAt: z.stringFormat("date-time", WIRE_OFFSET_DATETIME_PATTERN).nullable(),
  revokedBy: z.string().nullable(),
  /** Live (not removed) memes this person has published, and when they last published one. */
  memeCount: z.number().int().nonnegative(),
  lastUploadAt: z.stringFormat("date-time", WIRE_OFFSET_DATETIME_PATTERN).nullable(),
}).strict();
export const communityAdminMemeUploadersResponseSchema = z.object({
  uploaders: z.array(communityAdminMemeUploaderSchema).max(MEME_PAGE_SIZE),
  nextCursor: z.string().nullable(),
}).strict();
/** Which of up to 50 people currently hold an active uploader grant (search results, person drawer). */
export const communityAdminMemeUploaderStatusQuerySchema = z.object({
  personIds: z.string().transform((value) => value.split(",")).pipe(z.array(z.string().uuid()).min(1).max(50)),
}).strict();
export const communityAdminMemeUploaderStatusSchema = z.object({ active: z.array(z.string().uuid()).max(50) }).strict();
export type CommunityAdminMemeUploaderStatus = z.infer<typeof communityAdminMemeUploaderStatusSchema>;
export type CommunityAdminMeme = z.infer<typeof communityAdminMemeSchema>;
export type CommunityAdminMemesResponse = z.infer<typeof communityAdminMemesResponseSchema>;
export type CommunityAdminMemeUploader = z.infer<typeof communityAdminMemeUploaderSchema>;
export type CommunityAdminMemeUploadersResponse = z.infer<typeof communityAdminMemeUploadersResponseSchema>;
export type CommunityAdminMemeRemoveResponse = z.infer<typeof communityAdminMemeRemoveResponseSchema>;
export const communityAdminMemeUploaderMutationResponseSchema = z.object({ ok: z.literal(true) }).strict();
export type CommunityAdminMemeUploaderMutationResponse = z.infer<typeof communityAdminMemeUploaderMutationResponseSchema>;

/**
 * Admin wire contract of the community identity service (`apps/community`, routes under
 * `/v1/community/admin`) used by `apps/community-admin`. A separate wallet-signature session
 * requires an approved linked wallet/X identity; every mutation is recorded in the admin audit log.
 */

export const communityAdminSpecialBadgeIdSchema = z.enum(["special.squad", "special.the_squeekiest"]);
export const communityAdminSpecialBadgesSchema = z.object({
  badges: z.array(z.object({
    id: communityAdminSpecialBadgeIdSchema,
    title: z.string().min(1).max(100),
    earned: z.boolean(),
  }).strict()).length(2).refine((badges) => new Set(badges.map((badge) => badge.id)).size === 2, "Both special badges are required"),
}).strict();
export const communityAdminSpecialBadgeRequestSchema = z.object({ earned: z.boolean() }).strict();
export const communityAdminSpecialBadgeResponseSchema = z.object({ ok: z.literal(true) }).strict();
export type CommunityAdminSpecialBadgeId = z.infer<typeof communityAdminSpecialBadgeIdSchema>;
export type CommunityAdminSpecialBadges = z.infer<typeof communityAdminSpecialBadgesSchema>;
export type CommunityAdminSpecialBadgeRequest = z.infer<typeof communityAdminSpecialBadgeRequestSchema>;
export type CommunityAdminSpecialBadgeResponse = z.infer<typeof communityAdminSpecialBadgeResponseSchema>;

const dateTimeSchema = z.stringFormat("date-time", WIRE_OFFSET_DATETIME_PATTERN);
const decimalSchema = z.string().regex(/^(0|[1-9][0-9]{0,77})$/);
const positiveDecimalSchema = z.string().regex(/^[1-9][0-9]{0,77}$/);
const addressInputSchema = z.string().regex(/^0x[0-9a-fA-F]{40}$/);
const bytes32Schema = z.string().regex(/^0x[0-9a-f]{64}$/);
const sha256HexSchema = z.string().regex(/^[0-9a-f]{64}$/);
const snowflakeSchema = z.string().regex(/^[1-9][0-9]{14,20}$/);
const xUserIdSchema = z.string().regex(/^[1-9][0-9]{0,19}$/);
const uuidSchema = z.guid();
const countSchema = z.number().refine(Number.isInteger).meta({ type: "integer" }).nonnegative();
const reasonSchema = z.string().trim().min(1).max(500);
const chainIdSchema = z.number().int().positive().max(2_147_483_647);
const shortTextSchema = z.string().min(1).max(64);
const booleanParamSchema = z.enum(["true", "false"]).transform((value) => value === "true");
const countParamSchema = z.string().regex(/^(0|[1-9][0-9]{0,8})$/).transform(Number);

// ---------------------------------------------------------------------------------------------
// Admin-only wallet authentication; member sessions cannot authorize these routes.

export const communityAdminVerifyRequestSchema = communityVerifyRequestSchema.omit({ referralCode: true });

export const communityAdminIdentitySchema = z.object({
  personId: uuidSchema,
  walletAddress: communityAddressSchema,
  xUserId: xUserIdSchema,
}).strict();

export const communityAdminSessionSchema = z.object({
  identity: communityAdminIdentitySchema,
  expiresAt: dateTimeSchema,
}).strict();

export const communityAdminSessionResponseSchema = communityAdminSessionSchema.extend({
  token: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
});

// ---------------------------------------------------------------------------------------------
// Shared person pieces

export const communityAdminDisqualificationSchema = z.object({
  at: dateTimeSchema,
  reason: z.string().min(1).max(500),
  by: z.string().max(320).nullable(),
}).strict();

/** Why invitations stopped attributing: automatically (`by` null) or by an admin. */
export const communityAdminInviteFreezeSchema = z.object({
  at: dateTimeSchema,
  reason: z.string().min(1).max(500),
  by: z.string().max(320).nullable(),
}).strict();

export const communityAdminInvitesSchema = z.object({
  status: genesisInviteStatusSchema,
  frozen: communityAdminInviteFreezeSchema.nullable(),
  /** An admin lifted a freeze: automatic freezing skips this member until an admin freezes again. */
  freezeExempt: z.boolean(),
}).strict();

export const communityAdminXApprovalSchema = z.object({
  xUserId: xUserIdSchema,
  reason: z.string().min(3).max(500),
  by: z.string().min(1).max(320),
  at: dateTimeSchema,
}).strict();

export const communityAdminApproveXRequestSchema = z.object({
  xUserId: xUserIdSchema,
  reason: z.string().trim().min(3).max(500),
}).strict();

export const communityAdminRevokeXApprovalRequestSchema = communityAdminApproveXRequestSchema.pick({ reason: true });

export const communityAdminXApprovalResponseSchema = z.object({
  personId: uuidSchema,
  xApproval: communityAdminXApprovalSchema.nullable(),
}).strict();

export type CommunityAdminXApproval = z.infer<typeof communityAdminXApprovalSchema>;
export type CommunityAdminApproveXRequest = z.infer<typeof communityAdminApproveXRequestSchema>;
export type CommunityAdminRevokeXApprovalRequest = z.infer<typeof communityAdminRevokeXApprovalRequestSchema>;
export type CommunityAdminXApprovalResponse = z.infer<typeof communityAdminXApprovalResponseSchema>;

export const communityAdminProjectRemovalSchema = z.object({
  at: dateTimeSchema,
  by: z.string().min(1).max(320),
  reason: z.string().min(3).max(500),
}).strict();

/** The Squeekchain team's approval to show a verified project publicly; `by` is the admin actor. */
export const communityAdminProjectApprovalSchema = z.object({
  at: dateTimeSchema,
  by: z.string().min(1).max(320),
}).strict();

export const communityAdminHoldingSchema = z.object({
  collectionId: uuidSchema,
  chainId: chainIdSchema,
  collection: communityAddressSchema,
  /** Decimal ERC-1155 token id; `null` for ERC-721. */
  tokenId: decimalSchema.nullable(),
  label: z.string().max(128),
  gate: z.boolean(),
  /** Summed across the person's wallets at the latest snapshot of the chain. */
  balance: decimalSchema,
  observedAt: dateTimeSchema,
}).strict();

export const communityAdminReviewFlagSchema = z.object({
  at: dateTimeSchema,
  reason: z.string().min(1).max(500),
}).strict();

/** Admin-only observations, not proof of wallet ownership or a member eligibility gate. */
export const communityAdminWalletHistorySchema = z.object({
  /** No outgoing transactions on scanned supported chains; null without a scan. */
  walletEmpty: z.boolean().nullable(),
  /** No outgoing Ethereum transactions; null without an Ethereum scan. */
  noMainnet: z.boolean().nullable(),
  clusters: z.array(z.object({
    kind: z.enum(["nonce_twin", "shared_funder"]),
    inviterId: uuidSchema.nullable(),
    chainId: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive(),
    nonce: decimalSchema.nullable(),
    firstFunder: communityAddressSchema.nullable(),
    memberCount: countSchema,
  }).strict()),
}).strict();

export const communityAdminStandingSchema = z.object({
  rank: z.number().int().positive().nullable(),
  aura: countSchema,
  tier: genesisTierSchema.nullable(),
}).strict();
export type CommunityAdminStanding = z.infer<typeof communityAdminStandingSchema>;

export const communityAdminPersonSummarySchema = z.object({
  personId: uuidSchema,
  createdAt: dateTimeSchema,
  referralCode: z.string().min(1).max(16),
  profileImageUrl: communityXProfileImageSchema,
  standing: communityAdminStandingSchema.nullable(),
  x: z.object({
    userId: xUserIdSchema,
    username: shortTextSchema,
    name: z.string().max(128),
    /** When the X account was created; `null` when X did not report it. */
    createdAt: dateTimeSchema.nullable(),
    followers: countSchema.nullable(),
    posts: countSchema.nullable(),
    verified: z.boolean(),
    verifiedType: z.string().max(32).nullable(),
  }).strict().nullable(),
  xApproval: communityAdminXApprovalSchema.nullable(),
  discord: z.object({ userId: snowflakeSchema, username: shortTextSchema, createdAt: dateTimeSchema.nullable() }).strict().nullable(),
  walletCount: countSchema,
  /** Wallet that receives allowlist entries (explicit choice, else the first linked wallet). */
  mintWallet: communityAddressSchema.nullable(),
  ensName: z.string().max(255).nullable(),
  /** Earliest on-chain activity across the person's wallets; `null` until measured. */
  firstActivityAt: dateTimeSchema.nullable(),
  referrals: z.object({
    /** Ranking score: completed signups that also meet the X-account-age rule. */
    qualified: countSchema,
    /** Attributed signups whose referee has not linked X yet. */
    pending: countSchema,
    /** Attributed signups that completed wallet + X, regardless of the ranking age rule. */
    completed: countSchema,
  }).strict(),
  /** Known total balance of gate collections across chains and wallets. */
  gateBalance: decimalSchema,
  /**
   * A gate collection's read failed for one of the person's wallets at the latest snapshot: the
   * true gate balance may be higher than `gateBalance`.
   */
  gateHoldingUnknown: z.boolean(),
  holdings: z.array(communityAdminHoldingSchema),
  /** Display only (never an allowlist criterion): posts mentioning the project account by this X account. */
  mentions: countSchema,
  /** Display only (never an allowlist criterion): the X account follows the project account. */
  followsProject: z.boolean(),

  /** Transactions sent to Squeekchain testnet contracts; `null` while unmeasured. */
  testnetTxCount: countSchema.nullable(),
  /** Funding cluster the person belongs to, if any. */
  clusterId: z.string().min(1).max(128).nullable(),
  walletHistory: communityAdminWalletHistorySchema,
  disqualified: communityAdminDisqualificationSchema.nullable(),
  /** Genesis member criteria this person fails now; empty when they meet them. */
  criteriaFailures: z.array(genesisMemberCriteriaFailureSchema),
  invites: communityAdminInvitesSchema,
  /** Silent review flag (e.g. an invitation burst); never shown to the member. */
  flag: communityAdminReviewFlagSchema.nullable(),
}).strict();

// ---------------------------------------------------------------------------------------------
// People

export const communityAdminPeopleSortSchema = z.enum([
  "created_desc",
  "created_asc",
  "qualified_desc",
  "followers_desc",
  "mentions_desc",
  "gate_desc",
  "aura_desc",
]);

export const COMMUNITY_ADMIN_PAGE_SIZE = 50;

/** Query string of `GET /people`; every filter is optional. */
export const communityAdminPeopleQuerySchema = z.object({
  /** Matches X or Discord username, ENS name (substring), wallet address (prefix) or person id. */
  q: z.string().trim().min(1).max(128).optional(),
  hasX: booleanParamSchema.optional(),
  xApproved: booleanParamSchema.optional(),
  hasDiscord: booleanParamSchema.optional(),
  holdsGate: booleanParamSchema.optional(),
  minQualifiedReferrals: countParamSchema.optional(),
  followsProject: booleanParamSchema.optional(),
  minMentions: countParamSchema.optional(),
  /** `true`: at least one measured testnet transaction; `false`: measured zero or unmeasured. */
  testnetActive: booleanParamSchema.optional(),
  disqualified: booleanParamSchema.optional(),
  /** `true`: fails at least one Genesis member criterion (including no linked X). */
  belowCriteria: booleanParamSchema.optional(),
  invitesFrozen: booleanParamSchema.optional(),
  flagged: booleanParamSchema.optional(),
  inCluster: booleanParamSchema.optional(),
  /** Unknown (unscanned) histories match neither true nor false. */
  walletEmpty: booleanParamSchema.optional(),
  noMainnet: booleanParamSchema.optional(),
  /** Membership in a nonce-twin or shared-funder history cluster. */
  walletCluster: booleanParamSchema.optional(),
  sort: communityAdminPeopleSortSchema.optional(),
  cursor: z.string().regex(/^[A-Za-z0-9_-]{1,512}$/).optional(),
}).strict();

export const communityAdminPeoplePageSchema = z.object({
  items: z.array(communityAdminPersonSummarySchema).max(COMMUNITY_ADMIN_PAGE_SIZE),
  /** People matching the filters across all pages. */
  total: countSchema,
  nextCursor: z.string().nullable(),
}).strict();

export const communityAdminWalletSignalSchema = z.object({
  chainId: chainIdSchema,
  blockNumber: decimalSchema,
  nonce: decimalSchema,
  balanceWei: decimalSchema,
  ensName: z.string().max(255).nullable(),
  firstActivityAt: dateTimeSchema.nullable(),
  firstFunder: communityAddressSchema.nullable(),
  firstFundingTx: bytes32Schema.nullable(),
  firstFundingAt: dateTimeSchema.nullable(),
  squeekchainTxCount: countSchema.nullable(),
  nftCount: countSchema.nullable(),
  observedAt: dateTimeSchema,
}).strict();

export const communityAdminWalletNftSchema = z.object({
  chainId: chainIdSchema,
  contract: communityAddressSchema,
  name: z.string().max(256).nullable(),
  tokenCount: countSchema,
  isSpam: z.boolean(),
  observedAt: dateTimeSchema,
}).strict();

export const communityAdminDecisionKindSchema = z.enum(["include", "exclude"]);

export const communityAdminPersonDetailSchema = z.object({
  summary: communityAdminPersonSummarySchema,
  /** Live achievement evaluation for this person; reviewer-only, evidence references are not included. */
  achievements: communityAchievementsSchema,
  wallets: z.array(z.object({
    address: communityAddressSchema,
    kind: z.enum(["eoa", "contract"]),
    verifiedChainId: chainIdSchema,
    linkedAt: dateTimeSchema,
    mintWallet: z.boolean(),
    signals: z.array(communityAdminWalletSignalSchema),
    nfts: z.array(communityAdminWalletNftSchema),
  }).strict()),
  referrer: z.object({ personId: uuidSchema, xUsername: shortTextSchema.nullable() }).strict().nullable(),
  referees: z.array(z.object({
    personId: uuidSchema,
    xUsername: shortTextSchema.nullable(),
    createdAt: dateTimeSchema,
    qualified: z.boolean(),
    disqualified: z.boolean(),
    criteriaFailures: z.array(genesisMemberCriteriaFailureSchema),
  }).strict()),
  /** X accounts refused at link time for failing the member criteria, newest first. */
  xLinkRejections: z.array(z.object({
    xUserId: xUserIdSchema,
    username: shortTextSchema,
    failures: z.array(genesisMemberCriteriaFailureSchema).min(1),
    rejectedAt: dateTimeSchema,
  }).strict()),
  mentions: z.array(z.object({
    tweetId: xUserIdSchema,
    postedAt: dateTimeSchema,
    url: z.string().url(),
  }).strict()),
  funnel: z.array(z.object({
    step: z.string().max(64),
    outcome: z.string().max(64),
    errorCode: z.string().max(64).nullable(),
    walletName: z.string().max(64).nullable(),
    walletRdns: z.string().max(96).nullable(),
    os: z.string().max(64),
    browser: z.string().max(64),
    device: z.string().max(64),
    inApp: z.string().max(64),
    receivedAt: dateTimeSchema,
  }).strict()),
  allowlist: z.array(z.object({
    roundId: uuidSchema,
    roundName: z.string().min(1).max(128),
    status: z.enum(["draft", "frozen"]),
    decision: z.object({
      decision: communityAdminDecisionKindSchema,
      reason: z.string().max(500),
      decidedBy: z.string().max(320),
      decidedAt: dateTimeSchema,
    }).strict().nullable(),
    /** The frozen entry address; `null` for draft rounds or when not listed. */
    entryAddress: communityAddressSchema.nullable(),
  }).strict()),

  /** CODE submissions for abuse review (no judging); a project can be taken down from here. */
  projects: z.array(z.object({
    id: uuidSchema,
    origin: z.enum(["imd", "website", "independent"]),
    name: z.string().min(1).max(120),
    integration: z.string().min(20).max(1000),
    cover: codeCoverSchema.nullable(),
    updatedAt: dateTimeSchema,
    repoUrl: z.string().max(2048).nullable(),
    demoUrl: z.string().max(2048).nullable(),
    attributionKind: z.enum(["wallet", "domain", "repository"]),
    attributionStatus: z.enum(["pending", "verified", "failed"]),
    verifiedAt: dateTimeSchema.nullable(),
    checkedAt: dateTimeSchema.nullable(),
    createdAt: dateTimeSchema,
    removal: communityAdminProjectRemovalSchema.nullable(),
    approval: communityAdminProjectApprovalSchema.nullable(),
  }).strict()),
  /** Published holder badges with this person's claim, override or (winning or losing) candidates. */
  holders: z.array(holderAdminPersonBadgeSchema),
}).strict();

export const communityAdminDisqualifyRequestSchema = z.object({ reason: reasonSchema }).strict();

export const communityAdminDisqualificationResponseSchema = z.object({
  personId: uuidSchema,
  disqualified: communityAdminDisqualificationSchema.nullable(),
}).strict();

export const communityAdminInviteFreezeRequestSchema = z.object({ reason: reasonSchema }).strict();

export const communityAdminInvitesResponseSchema = z.object({
  personId: uuidSchema,
  invites: communityAdminInvitesSchema,
}).strict();

/** `POST /people/:id/flag/clear`: a reviewer dismisses the review flag, with a reason for the audit. */
export const communityAdminClearFlagRequestSchema = z.object({ reason: reasonSchema }).strict();

export const communityAdminFlagResponseSchema = z.object({
  personId: uuidSchema,
  flag: communityAdminReviewFlagSchema.nullable(),
}).strict();

/** Most people a tree disqualification lists; `count` is always the full number. */
export const COMMUNITY_ADMIN_TREE_LIST_LIMIT = 500;

/**
 * Disqualifies an inviter and every invitee below them, at any depth, who fails the member criteria
 * (descendants who meet them stay). Without `confirmCount` it only previews; with it, it applies
 * when the tree still holds exactly that many people to disqualify, else 409 `tree_changed`.
 */
export const communityAdminTreeDisqualifyRequestSchema = z.object({
  reason: reasonSchema,
  confirmCount: countSchema.optional(),
}).strict();

export const communityAdminTreeDisqualifyResponseSchema = z.object({
  rootPersonId: uuidSchema,
  applied: z.boolean(),
  /** People disqualified (or to disqualify), the root included unless already disqualified. */
  count: countSchema,
  people: z.array(z.object({
    personId: uuidSchema,
    xUsername: shortTextSchema.nullable(),
    /** 0 for the root, 1 for direct invitees, and so on. */
    depth: countSchema,
    criteriaFailures: z.array(genesisMemberCriteriaFailureSchema),
  }).strict()).max(COMMUNITY_ADMIN_TREE_LIST_LIMIT),
}).strict();

// ---------------------------------------------------------------------------------------------
// CODE projects

/** Stored by the `code-review-signals` job from its latest refreshes (`domain_reregistered` is sticky). */
export const communityAdminReviewSignalFlagSchema = z.enum([
  "domain_new", "domain_expired", "domain_reregistered",
  "site_failing", "site_parked", "marker_missing", "content_changed",
  "repo_missing", "repo_not_repository", "repo_not_found", "repo_new", "repo_owner_new", "repo_few_commits", "repo_fork",
  "repo_proof_missing", "repo_no_reference", "repo_model_concern",
  "imd_default_name", "imd_prompt_copied", "imd_duplicate_objective", "imd_unusual_template",
  "imd_review_failed", "imd_no_repo", "imd_scan_incomplete", "imd_no_reference", "imd_model_unavailable", "imd_model_concern",
]);

/** Host hints set when a website project is saved: a label contains `squeek`, or is an `xn--` IDN. */
export const communityAdminHostReviewFlagSchema = z.enum(["brand_lookalike", "idn"]);
/**
 * Filter of `GET /projects`. `verified`, `pending` and `failed` are attribution states of projects
 * that are not removed; `awaiting_review` includes verified and pending IMD projects that are not
 * removed or approved; `removed` is every removed project whatever its attribution.
 */
export const communityAdminProjectStatusFilterSchema = z.enum([
  "all", "awaiting_review", "verified", "pending", "failed", "declined", "removed",
]);

export const communityAdminProjectsQuerySchema = z.object({
  status: communityAdminProjectStatusFilterSchema.optional(),
  q: z.string().trim().min(1).max(100).optional(),
  flags: z.string().refine((value) => {
    const flags = value.split(",");
    return flags.length <= 8 && flags.every((flag) => communityAdminReviewSignalFlagSchema.safeParse(flag).success);
  }).optional(),
  cursor: z.string().regex(/^[A-Za-z0-9_-]{1,512}$/).optional(),
}).strict();

/**
 * One project as the admin sees it; `removal.by` and `approval.by` are admin actors and never reach
 * members. Only a verified, approved, not removed project is shown publicly.
 */
export const communityAdminProjectSchema = z.object({
  id: uuidSchema,
  origin: z.enum(["imd", "website", "independent"]),
  name: z.string().min(1).max(120),
  integration: z.string().min(20).max(1000),
  cover: codeCoverSchema.nullable(),
  updatedAt: dateTimeSchema,
  owner: z.object({
    personId: uuidSchema,
    xUsername: shortTextSchema.nullable(),
    referralCode: z.string().min(1).max(16),
    xName: z.string().max(128).nullable(),
    profileImageUrl: communityXProfileImageSchema,
    xCreatedAt: dateTimeSchema.nullable(),
    xFollowers: countSchema.nullable(),
    standing: communityAdminStandingSchema.nullable(),
    disqualified: z.boolean(),
    flagged: z.boolean(),
    invitesFrozen: z.boolean(),
    criteriaFailures: z.array(genesisMemberCriteriaFailureSchema),
  }).strict(),
  /** Attribution state; unchanged by removal or approval. */
  status: z.enum(["pending", "verified", "failed"]),
  removal: communityAdminProjectRemovalSchema.nullable(),
  approval: communityAdminProjectApprovalSchema.nullable(),
  verifiedAt: dateTimeSchema.nullable(),
  createdAt: dateTimeSchema,
  demoUrl: z.string().max(2048).nullable(),
  repoUrl: z.string().max(2048).nullable(),
  /** A website project's verified-or-claimed host. */
  domain: z.string().max(253).nullable(),
  /** An IMD project's latest job id. */
  imdJobId: z.string().max(512).nullable(),
  /** Advisory IMD ranking, never an approval or verification. */
  reviewScore: z.number().refine(Number.isInteger).meta({ type: "integer" }).nullable(),
  review: z.object({
    flags: z.array(communityAdminReviewSignalFlagSchema).max(32),
    hostFlags: z.array(communityAdminHostReviewFlagSchema).max(8),
    modelStatus: z.enum(["ok", "not_configured", "error", "skipped"]).nullable(),
    verdict: z.object({
      usesSqueekchain: z.boolean(), appearsFunctional: z.boolean(),
      /** Absent on assessments stored before the model was asked whether the code is the described project. */
      matchesProject: z.boolean().optional(),
      effort: z.number().int().min(1).max(5), reason: z.string().max(300),
    }).strict().nullable(),
    references: countSchema.nullable(),
    scanComplete: z.boolean().nullable(),
    repoDelivered: z.boolean().nullable(),
    reviewedAt: dateTimeSchema.nullable(),
  }).strict().nullable(),
  decline: z.object({ at: dateTimeSchema, by: z.string(), reason: z.string() }).strict().nullable(),
}).strict();

/** Awaiting review: IMD score descending (nulls last), then newest submission and id; otherwise newest first. */
export const communityAdminProjectsPageSchema = z.object({
  items: z.array(communityAdminProjectSchema).max(COMMUNITY_ADMIN_PAGE_SIZE),
  nextCursor: z.string().nullable(),
}).strict();

export const communityAdminProjectCountsSchema = z.object({
  awaiting_review: countSchema, all: countSchema, verified: countSchema,
  pending: countSchema, failed: countSchema, declined: countSchema, removed: countSchema,
}).strict();

export const communityAdminDeclineProjectRequestSchema = z.object({
  updatedAt: dateTimeSchema, reason: z.string().trim().min(3).max(500),
}).strict();

export const communityAdminProjectBatchRequestSchema = z.object({
  action: z.enum(["approve", "decline"]),
  items: z.array(z.object({ id: uuidSchema, updatedAt: dateTimeSchema }).strict()).min(1).max(50),
  reason: z.string().trim().min(3).max(500).optional(),
}).strict().superRefine((value, ctx) => {
  if (value.action === "decline" && !value.reason) {
    ctx.addIssue({ code: "custom", path: ["reason"], message: "Declining requires a reason" });
  }
});

export const communityAdminProjectBatchResponseSchema = z.object({
  results: z.array(z.object({
    id: uuidSchema, ok: z.boolean(), error: z.string().nullable(), project: communityAdminProjectSchema.nullable(),
  }).strict()),
}).strict();

/**
 * Takes a project down without disqualifying its member: it leaves the public showcase and stops
 * counting toward The Buildooooor; build places already taken are never reassigned.
 */
export const communityAdminRemoveProjectRequestSchema = z.object({
  reason: z.string().trim().min(3).max(500),
}).strict();

/**
 * Approves the reviewed version for CODE rewards and lists direct submissions publicly.
 * Verified IMD projects are already listed. The reason is an optional audit note.
 */
export const communityAdminApproveProjectRequestSchema = z.object({
  updatedAt: dateTimeSchema,
  reason: z.string().trim().min(3).max(500).optional(),
}).strict();

/** Withdraws reward approval; direct submissions leave public listings, but verified IMD projects stay listed. */
export const communityAdminUnapproveProjectRequestSchema = z.object({
  reason: z.string().trim().min(3).max(500),
}).strict();


const reviewPersonSchema = z.object({
  personId: uuidSchema,
  xUsername: shortTextSchema.nullable(),
  referralCode: z.string().min(1).max(16),
}).strict();

/** Optional Workers AI opinion: advisory only, never automatic verification. */
export const communityAdminModelReviewSchema = z.object({
  status: z.enum(["ok", "not_configured", "error", "skipped"]),
  model: z.string().nullable(),
  commit: z.string(),
  verdict: z.object({
    usesSqueekchain: z.boolean(),
    appearsFunctional: z.boolean(),
    /** Absent on assessments stored before the model was asked whether the code is the described project. */
    matchesProject: z.boolean().optional(),
    effort: z.number(),
    reason: z.string(),
  }).strict().nullable(),
  error: z.string().nullable(),
  reviewedAt: dateTimeSchema,
}).strict();

/**
 * `GET /projects/:id/review`: what the Squeekchain team weighs before approving a project. Read-only;
 * nothing here loads the member's site. `domain`, `site` and `repo` are `null` until the job has
 * refreshed them; `site.textExcerpt` is the start of the latest normalized visible text.
 */
export const communityAdminProjectReviewSchema = z.object({
  projectId: uuidSchema,
  hostFlags: z.array(communityAdminHostReviewFlagSchema).max(8),
  flags: z.array(communityAdminReviewSignalFlagSchema).max(32),
  reviewScore: z.number().refine(Number.isInteger).meta({ type: "integer" }).nullable(),
  scoreBreakdown: z.array(z.object({
    key: z.string().max(64), label: z.string().max(120), points: z.number().refine(Number.isInteger).meta({ type: "integer" }),
  }).strict()).max(32).nullable(),
  imdReviewKey: z.string().nullable(),
  imdSignals: z.record(z.string(), z.unknown()).nullable(),
  modelReview: communityAdminModelReviewSchema.nullable(),
  imdReviewedAt: dateTimeSchema.nullable(),
  /**
   * A direct submission's source scan (Squeekchain references in its repository's text files, with
   * excerpts) and when it and the model last assessed the repository; `null` until then.
   */
  sourceScan: z.record(z.string(), z.unknown()).nullable(),
  sourceReviewedAt: dateTimeSchema.nullable(),
  domain: z.object({
    registeredDomain: z.string().max(253).nullable(),
    registeredAt: dateTimeSchema.nullable(),
    expiresAt: dateTimeSchema.nullable(),
    registrar: z.string().max(200).nullable(),
    /** Whole days from registration to now. */
    ageDays: countSchema.nullable(),
    checkedAt: dateTimeSchema,
  }).strict().nullable(),
  site: z.object({
    checkedAt: dateTimeSchema,
    result: z.enum(["ok", "unreachable", "not_https", "bad_status", "too_large"]),
    status: z.number().int().min(100).max(599).nullable(),
    finalUrl: z.string().max(4096).nullable(),
    title: z.string().max(1024).nullable(),
    markerFound: z.boolean().nullable(),
    parked: z.boolean().nullable(),
    contentHash: sha256HexSchema.nullable(),
    textExcerpt: z.string().max(2000).nullable(),
    consecutiveFailures: countSchema,
    /** The approval the text baseline belongs to; `null` without one. */
    baselineApprovedAt: dateTimeSchema.nullable(),
    /** Word-set Jaccard similarity to the baseline at the latest check. */
    similarity: z.number().min(0).max(1).nullable(),
  }).strict().nullable(),
  repo: z.object({
    url: z.string().max(2048).nullable(),
    host: z.string().max(253).nullable(),
    owner: z.string().max(100).nullable(),
    name: z.string().max(100).nullable(),
    error: z.enum(["not_found", "unavailable"]).nullable(),
    createdAt: dateTimeSchema.nullable(),
    pushedAt: dateTimeSchema.nullable(),
    sizeKb: countSchema.nullable(),
    fork: z.boolean().nullable(),
    commitCount: countSchema.nullable(),
    /** The first commit page was full: at least `commitCount` commits. */
    commitCountCapped: z.boolean().nullable(),
    ownerCreatedAt: dateTimeSchema.nullable(),
    ownerPublicRepos: countSchema.nullable(),
    /**
     * Direct submissions: whether the repository's README or `squeekchain.json` carries the project's
     * id at the latest check (`null` when it couldn't be read), and when it was first seen there.
     */
    proof: z.enum(["found", "missing"]).nullable(),
    proofSeenAt: dateTimeSchema.nullable(),
    checkedAt: dateTimeSchema,
  }).strict().nullable(),
  member: reviewPersonSchema.extend({
    signedUpAt: dateTimeSchema,
    disqualified: z.boolean(),
    xCreatedAt: dateTimeSchema.nullable(),
    xFollowers: countSchema.nullable(),
    walletCount: countSchema,
    /** Who invited the member, then who invited them (at most three). */
    inviters: z.array(reviewPersonSchema.extend({ projectCount: countSchema })).max(3),
    cluster: z.object({
      id: z.string().min(1).max(128),
      reason: z.enum(["shared_funder", "funded_by_member"]),
      size: countSchema,
    }).strict().nullable(),
  }).strict(),
  /** Other members' website projects whose domain was registered within 3 h, or same registrar and day. */
  siblings: z.array(z.object({
    projectId: uuidSchema,
    name: z.string().min(1).max(120),
    domain: z.string().max(253).nullable(),
    owner: reviewPersonSchema,
    registeredAt: dateTimeSchema.nullable(),
    registrar: z.string().max(200).nullable(),
    match: z.enum(["registered_within_3h", "same_registrar_same_day"]),
  }).strict()).max(50),
  /** Other projects whose description shares at least 60% of its words (word-set Jaccard). */
  similar: z.array(z.object({
    projectId: uuidSchema,
    name: z.string().min(1).max(120),
    owner: reviewPersonSchema,
    similarity: z.number().min(0).max(1),
  }).strict()).max(50),
}).strict();

// ---------------------------------------------------------------------------------------------
// Overview and jobs

export const communityAdminJobRunSchema = z.object({
  id: decimalSchema,
  job: z.string().min(1).max(64),
  startedAt: dateTimeSchema,
  finishedAt: dateTimeSchema.nullable(),
  status: z.enum(["running", "succeeded", "failed", "skipped"]),
  detail: z.record(z.string(), z.unknown()),
}).strict();

export const communityAdminSnapshotSchema = z.object({
  id: uuidSchema,
  chainId: chainIdSchema,
  blockNumber: decimalSchema,
  blockHash: bytes32Schema,
  blockTimestamp: dateTimeSchema,
  manifestSha256: sha256HexSchema,
  walletCount: countSchema,
  unknownCount: countSchema,
  takenAt: dateTimeSchema,
}).strict();

export const communityAdminLatestJobSchema = z.object({
  job: z.string().min(1).max(64),
  run: communityAdminJobRunSchema.nullable(),
}).strict();

export const communityAdminOverviewSchema = z.object({
  totals: z.object({
    people: countSchema,
    withX: countSchema,
    withDiscord: countSchema,
    wallets: countSchema,
    qualifiedReferrals: countSchema,
    disqualified: countSchema,
  }).strict(),
  /** Latest holdings snapshot per chain. */
  snapshots: z.array(communityAdminSnapshotSchema),
  jobs: z.array(communityAdminLatestJobSchema),
  generatedAt: dateTimeSchema,
}).strict();

export const communityAdminJobsSchema = z.object({
  latest: z.array(communityAdminLatestJobSchema),
  failures: z.array(communityAdminJobRunSchema).max(50),
}).strict();

// ---------------------------------------------------------------------------------------------
// Referrals, graph, clusters

export const communityAdminReferralTreeSchema = z.object({
  /** Everyone who referred or was referred. Roots have `referredBy: null`. */
  nodes: z.array(z.object({
    personId: uuidSchema,
    username: shortTextSchema.nullable(),
    referredBy: uuidSchema.nullable(),
    /** This person's own sign-up counts as a qualified referral for their referrer. */
    qualified: z.boolean(),
    disqualified: z.boolean(),
    qualifiedReferrals: countSchema,
    pendingReferrals: countSchema,
  }).strict()),
  edges: z.array(z.object({ from: uuidSchema, to: uuidSchema, qualified: z.boolean() }).strict()),
}).strict();

export const communityAdminGraphSchema = z.object({
  nodes: z.array(z.object({
    personId: uuidSchema,
    label: z.string().min(1).max(64),
    clusterId: z.string().min(1).max(128).nullable(),
    qualifiedReferrals: countSchema,
    disqualified: z.boolean(),
    hasX: z.boolean(),
  }).strict()),
  edges: z.array(z.object({
    kind: z.enum(["referral", "funding"]),
    source: uuidSchema,
    target: uuidSchema,
    clusterId: z.string().min(1).max(128).nullable(),
  }).strict()),
  /** People with neither a referral nor a funding-cluster edge, left out of `nodes`. */
  isolated: countSchema,
}).strict();

export const communityAdminClustersSchema = z.object({
  clusters: z.array(z.object({
    id: z.string().min(1).max(128),
    reason: z.enum(["shared_funder", "funded_by_member"]),
    funders: z.array(communityAddressSchema),
    members: z.array(z.object({
      personId: uuidSchema,
      xUsername: shortTextSchema.nullable(),
      disqualified: z.boolean(),
      qualifiedReferrals: countSchema,
    }).strict()),
  }).strict()),
}).strict();

// ---------------------------------------------------------------------------------------------
// Funnel and coverage

export const COMMUNITY_ADMIN_COVERAGE_THRESHOLD = 0.8;

export const communityAdminFunnelQuerySchema = z.object({
  days: z.string().regex(/^[1-9][0-9]?$/).transform(Number).refine((days) => days <= 90).optional(),
}).strict();

const stepRateSchema = z.object({
  /** Clients in the combination with any event for the step. */
  attempted: countSchema,
  succeeded: countSchema,
  /** `succeeded / attempted`; `null` without an attempt. */
  rate: z.number().min(0).max(1).nullable(),
}).strict();

export const communityAdminFunnelSchema = z.object({
  days: z.number().int().min(1).max(90),
  since: dateTimeSchema,
  distinctClients: countSchema,
  steps: z.array(z.object({
    step: communityFunnelStepSchema,
    outcome: communityFunnelOutcomeSchema,
    events: countSchema,
    clients: countSchema,
  }).strict()),
  /**
   * One row per environment and wallet a client used: a client in two environments counts in
   * both. Sorted by clients (desc); shares are of the sum of `clients` over all rows.
   */
  combinations: z.array(z.object({
    os: z.string().max(64),
    browser: z.string().max(64),
    device: z.string().max(64),
    inApp: z.string().max(64),
    /** Last wallet the client reported in this environment; `null` when none was reported. */
    walletName: z.string().max(64).nullable(),
    walletRdns: z.string().max(96).nullable(),
    clients: countSchema,
    share: z.number().min(0).max(1),
    cumulativeShare: z.number().min(0).max(1),
    /** Part of the smallest prefix of rows whose cumulative share reaches the threshold. */
    inCoverageSet: z.boolean(),
    walletSign: stepRateSchema,
    xLink: stepRateSchema,
  }).strict()),
  coverage: z.object({
    threshold: z.number().min(0).max(1),
    combinations: countSchema,
    totalCombinations: countSchema,
  }).strict(),
}).strict();

// ---------------------------------------------------------------------------------------------
// Collections and Discord role rules

export const communityAdminCollectionSchema = z.object({
  id: uuidSchema,
  chainId: chainIdSchema,
  address: communityAddressSchema,
  standard: z.enum(["erc721", "erc1155"]),
  tokenId: decimalSchema.nullable(),
  label: z.string().min(1).max(128),
  gate: z.boolean(),
  createdAt: dateTimeSchema,
  roleRules: countSchema,
}).strict();

export const communityAdminCollectionsSchema = z.object({
  collections: z.array(communityAdminCollectionSchema),
}).strict();

export const communityAdminCreateCollectionRequestSchema = z.object({
  chainId: chainIdSchema,
  address: addressInputSchema,
  standard: z.enum(["erc721", "erc1155"]),
  /** Required for ERC-1155, absent for ERC-721. */
  tokenId: decimalSchema.optional(),
  label: z.string().trim().min(1).max(128),
  gate: z.boolean(),
}).strict().refine((value) => (value.standard === "erc1155") === (value.tokenId !== undefined), {
  message: "tokenId is required for erc1155 and forbidden for erc721",
  path: ["tokenId"],
});

export const communityAdminRoleRuleSchema = z.object({
  id: uuidSchema,
  roleId: snowflakeSchema,
  collectionId: uuidSchema,
  collectionLabel: z.string().min(1).max(128),
  minBalance: positiveDecimalSchema,
  createdAt: dateTimeSchema,
}).strict();

export const communityAdminRoleRulesSchema = z.object({
  rules: z.array(communityAdminRoleRuleSchema),
}).strict();

export const communityAdminCreateRoleRuleRequestSchema = z.object({
  roleId: snowflakeSchema,
  collectionId: uuidSchema,
  minBalance: positiveDecimalSchema,
}).strict();

// ---------------------------------------------------------------------------------------------
// Allowlist

/**
 * Criteria proposing allowlist candidates. Absent fields do not constrain. Posts, mentions and
 * follows are never criteria: rewards attach only to referral sign-ups.
 */
export const communityAllowlistCriteriaV1Schema = z.object({
  version: z.literal(1),
  /** X is always required for the allowlist. */
  requireX: z.literal(true),
  minXAccountAgeDays: z.number().int().min(0).max(36_500).optional(),
  minQualifiedReferrals: z.number().int().min(0).max(1_000_000).optional(),
  /** Known gate balance > 0; an unknown read is never treated as zero (`gate_holding_unknown`). */
  requireGateHolding: z.boolean().optional(),
  requireDiscord: z.boolean().optional(),
  requireTestnetActivity: z.boolean().optional(),
  /**
   * Seats in the list. Manual includes take seats first; the rest go to eligible candidates ranked
   * by qualified referrals, then known gate holdings, then earliest sign-up.
   */
  maxEntries: z.number().int().positive().max(1_000_000).optional(),
}).strict();

/**
 * Seats in a Genesis whitelist round: the sale supply. Membership itself is uncapped; only the frozen
 * list is bounded (the sale contract would accept up to 9,999).
 */
export const GENESIS_ALLOWLIST_MAX_ENTRIES = 8_888;

/**
 * Genesis campaign criteria: candidates rank by Aura in the latest snapshot (ADR 0028). Members in
 * that snapshot (signup complete, not disqualified) are eligible: Signed Up is worth
 * 1 Aura, so every one of them has Aura (D13). Manual includes (direct game winners) take seats
 * first and are Gold; the rest take tiers by their place among the included active members (D14),
 * or Member with signup badges only. Frozen v1 rounds are unaffected.
 */
export const communityAllowlistCriteriaV2Schema = z.object({
  version: z.literal(2),
  requireX: z.literal(true),
  maxEntries: z.number().int().positive().max(GENESIS_ALLOWLIST_MAX_ENTRIES),
  /** Whole-number Aura, as shown publicly. Every ranked member has at least 1 (Signed Up). */
  minAura: z.number().int().min(0).max(1_000_000).optional(),
  /** Eligible only with at least one of these achievements earned. */
  anyAchievement: z.array(z.string().regex(/^[a-z][a-z0-9]*(\.[a-z0-9_]+)+$/)).min(1).max(32).optional(),
  minXAccountAgeDays: z.number().int().min(0).max(36_500).optional(),
  requireDiscord: z.boolean().optional(),
  /** Gold and Silver shares of the included active members; defaults to `GENESIS_DEFAULT_TIER_SHARES`. */
  tierShares: genesisTierSharesSchema.optional(),
}).strict();

export const communityAllowlistCriteriaSchema = z.discriminatedUnion("version", [
  communityAllowlistCriteriaV1Schema,
  communityAllowlistCriteriaV2Schema,
]);

export const communityAllowlistReasonSchema = z.enum([
  /** Included: meets every criterion. */
  "criteria",
  /** Included: manual include decision. */
  "manual_include",
  /** Included: top five eligible Exsqueektion Live member places. */
  "exsqueektion_top5",
  /** Included: an admin-granted special badge guarantees a Gold place in the whitelist, subject to identity blocks. */
  "special_badge",
  "disqualified",
  /** A manual include cannot override disqualification. */
  "include_blocked_by_disqualification",
  "no_x",
  /** A manual include cannot override the X requirement. */
  "include_blocked_no_x",
  "no_wallet",
  /** The mint wallet is a contract wallet proven on another chain than the round's. */
  "mint_wallet_other_chain",
  "manual_exclude",
  "x_account_too_new",
  "x_account_age_unknown",
  "include_blocked_x_too_new",
  "insufficient_referrals",
  "no_gate_holding",
  /** No known gate holding and a gate read failed: unknown, never treated as zero. */
  "gate_holding_unknown",
  "no_discord",
  "discord_account_too_new",
  "no_testnet_activity",
  "testnet_activity_unknown",
  "over_max_entries",
  /** v2: not in the Aura snapshot the round ranks by (not yet computed). */
  "not_in_aura_snapshot",
  "insufficient_aura",
  "no_required_achievement",
]);

export const communityAllowlistLeafFormatSchema = z.object({
  id: z.string().min(1).max(64),
  description: z.string().max(500),
  leafEncoding: z.array(z.string().min(1).max(32)).min(1),
}).strict();

export const communityAdminRoundStatusSchema = z.enum(["draft", "frozen"]);

export const communityAdminRoundSchema = z.object({
  id: uuidSchema,
  name: z.string().min(1).max(128),
  chainId: chainIdSchema,
  collectionAddress: communityAddressSchema,
  leafFormat: z.string().min(1).max(64),
  criteria: communityAllowlistCriteriaSchema,
  status: communityAdminRoundStatusSchema,
  published: z.boolean(),
  root: bytes32Schema.nullable(),
  entryCount: countSchema.nullable(),
  manifestSha256: sha256HexSchema.nullable(),
  /** Holdings snapshots (one per chain) the frozen evaluation read; empty for a draft round. */
  snapshotIds: z.array(uuidSchema),
  /** Aura snapshot a frozen v2 round ranked by; `null` for drafts and v1 rounds. */
  auraSnapshotId: decimalSchema.nullable(),
  createdBy: z.string().max(320),
  createdAt: dateTimeSchema,
  frozenBy: z.string().max(320).nullable(),
  frozenAt: dateTimeSchema.nullable(),
}).strict();

export const communityAdminRoundsSchema = z.object({
  rounds: z.array(communityAdminRoundSchema),
  leafFormats: z.array(communityAllowlistLeafFormatSchema),
}).strict();

export const communityAdminCreateRoundRequestSchema = z.object({
  name: z.string().trim().min(1).max(128),
  chainId: chainIdSchema,
  collectionAddress: addressInputSchema,
  leafFormat: z.string().min(1).max(64),
  criteria: communityAllowlistCriteriaSchema.optional(),
}).strict();

export const communityAdminDecisionRequestSchema = z.object({
  personId: uuidSchema,
  decision: communityAdminDecisionKindSchema,
  reason: reasonSchema,
}).strict();

export const communityAdminDecisionSchema = z.object({
  personId: uuidSchema,
  xUsername: shortTextSchema.nullable(),
  decision: communityAdminDecisionKindSchema,
  reason: z.string().min(1).max(500),
  decidedBy: z.string().max(320),
  decidedAt: dateTimeSchema,
}).strict();

export const communityAdminCandidateSchema = z.object({
  personId: uuidSchema,
  xUsername: shortTextSchema.nullable(),
  /** Address that would enter the tree (the person's mint wallet). */
  address: communityAddressSchema.nullable(),
  included: z.boolean(),
  /** Inclusion reason, or every reason the person is excluded. */
  reasons: z.array(communityAllowlistReasonSchema).min(1),
  /** Position among criteria-eligible candidates; `null` for others. */
  rank: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive().nullable(),
  decision: communityAdminDecisionKindSchema.nullable(),
  qualifiedReferrals: countSchema,
  gateBalance: decimalSchema,
  gateHoldingUnknown: z.boolean(),
  /** Aura rank and whole-number Aura in the round's snapshot; `null` outside it or for v1 rounds. */
  auraRank: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive().nullable(),
  aura: countSchema.nullable(),
  /** Whitelist tier of an included v2 candidate; `null` otherwise. */
  tier: genesisTierSchema.nullable(),
}).strict();

export const communityAdminEntrySchema = z.object({
  personId: uuidSchema,
  xUsername: shortTextSchema.nullable(),
  address: communityAddressSchema,
  reason: z.enum(["criteria", "manual_include", "exsqueektion_top5", "special_badge"]),
  /** Frozen tier and whitelist buying limit; `null` for rounds without tiers (v1 criteria). */
  tier: genesisTierSchema.nullable(),
  entryLimit: z.number().int().min(1).max(10).nullable(),
}).strict();

export const communityAdminRoundDetailSchema = z.object({
  round: communityAdminRoundSchema,
  decisions: z.array(communityAdminDecisionSchema),
  /** Live evaluation of a draft round; `null` once frozen. */
  evaluation: z.object({
    counts: z.object({ candidates: countSchema, included: countSchema, excluded: countSchema }).strict(),
    /** What freezing now would produce; `null` without an included candidate. */
    preview: z.object({ entryCount: countSchema, root: bytes32Schema }).strict().nullable(),
    /** Holdings snapshots (latest per chain) this evaluation read. */
    snapshotIds: z.array(uuidSchema),
    /** Aura snapshot a v2 evaluation ranked by; `null` for v1 or before the first snapshot. */
    auraSnapshotId: decimalSchema.nullable(),
    /** Non-disqualified candidates with a failed gate read at those snapshots. */
    gateHoldingUnknown: countSchema,
    /**
     * Why freezing is refused now; `holdings_unknown` while a failed gate read could change who
     * qualifies or who gets a seat (`requireGateHolding` or `maxEntries` set);
     * `entry_limits_unavailable` when the leaf format carries a per-wallet limit
     * (`genesis-launch-v1`) but the criteria assign no tiers (v1).
     */
    freezeBlocked: z.enum(["holdings_unknown", "aura_snapshot_missing", "aura_snapshot_stale", "entry_limits_unavailable"]).nullable(),
    candidates: z.array(communityAdminCandidateSchema),
  }).strict().nullable(),
  /** Frozen membership; empty for a draft round. */
  entries: z.array(communityAdminEntrySchema),
}).strict();

/** Optional on legacy DELETE; review clients record why they restored criteria. */
export const communityAdminRemoveDecisionRequestSchema = z.object({ reason: reasonSchema }).strict();

/** Freezing must confirm exactly what the admin reviewed. */
export const communityAdminFreezeRequestSchema = z.object({
  confirmEntryCount: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive(),
  confirmRoot: bytes32Schema,
}).strict();

/**
 * Seats direct game winners: each wallet is matched to the full member (wallet and X, not
 * disqualified) who verified it, and becomes a manual include with one shared reason. Wallets
 * without such a member are reported and not seated; they can be re-entered once the person joins.
 */
export const communityAdminWinnersRequestSchema = z.object({
  wallets: z.array(addressInputSchema).min(1).max(1_000),
  reason: reasonSchema,
}).strict();

export const communityAdminWinnersResponseSchema = z.object({
  seated: z.array(z.object({
    wallet: communityAddressSchema,
    personId: uuidSchema,
    xUsername: shortTextSchema.nullable(),
    /** The address that enters the tree: the member's chosen mint wallet. */
    entryAddress: communityAddressSchema.nullable(),
  }).strict()),
  unmatched: z.array(z.object({
    wallet: communityAddressSchema,
    reason: z.enum(["no_member", "no_x", "disqualified", "x_account_too_new"]),
  }).strict()),
}).strict();

/**
 * A frozen round's whitelist for the sale's proof-bundle builder. Each leaf is
 * `[chainId, launch, account, entryLimit]` (`genesis-launch-v1`); `root` and `memberCount` must
 * equal what the bundle builder computes and what the guardian publishes on chain.
 */
export const communityAdminRoundExportSchema = z.object({
  schemaVersion: z.literal(1),
  roundId: uuidSchema,
  chainId: chainIdSchema,
  launch: communityAddressSchema,
  leafFormat: z.string().min(1).max(64),
  root: bytes32Schema,
  memberCount: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive(),
  manifestSha256: sha256HexSchema,
  /** Sorted by lowercase account, unique. */
  entries: z.array(z.object({
    account: communityAddressSchema,
    entryLimit: z.number().int().min(1).max(10),
  }).strict()).min(1),
}).strict();

// ---------------------------------------------------------------------------------------------
// X Gateway owns project-account broadcast chat and command delivery.
// Community provides the admin proxy; the game service decides admission and rewards.

export const communityAdminStreamEventTypeSchema = z.enum([
  "broadcast.chat",
  "broadcast.chat.remove",
  "broadcast.start",
  "broadcast.end",
]);

const xNumericIdSchema = z.string().regex(/^[0-9]{1,19}$/);

export const communityAdminStreamBroadcastSchema = z.object({
  broadcastId: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/),
  title: z.string().max(512).nullable(),
  /** `unknown` until a broadcast.start or broadcast.end event named it. */
  state: z.enum(["unknown", "running", "ended"]),
  startedAt: dateTimeSchema.nullable(),
  endedAt: dateTimeSchema.nullable(),
  firstSeenAt: dateTimeSchema,
  /** Messages not removed by the host or a moderator. */
  messages: countSchema,
  removedMessages: countSchema,
  /** Distinct authors of messages not removed. */
  chatters: countSchema,
  /** Chatters whose X account is linked to a community member. */
  memberChatters: countSchema,
}).strict();

/** Gateway-owned command delivery counters; community has no chat database. */
export const communityAdminStreamCommandsSchema = z.object({
  enabled: z.boolean(),
  pending: countSchema,
  accepted: countSchema,
  rejected: countSchema,
  removed: countSchema,
}).strict();

export const communityAdminStreamStatusSchema = z.object({
  /** X_STREAM_OWNER_USER_ID, X_APP_BEARER_TOKEN, COMMUNITY_TOKEN_KEY and the X OAuth client are set. */
  configured: z.boolean(),
  ownerUserId: xUserIdSchema.nullable(),
  /** Register both in the X app: the OAuth 2.0 callback and the webhook URL. */
  callbackUrl: z.string().url(),
  webhookUrl: z.string().url(),
  requiredScopes: z.array(z.string().max(64)),
  owner: z.object({
    userId: xUserIdSchema,
    username: shortTextSchema,
    scopes: z.array(z.string().max(64)),
    accessExpiresAt: dateTimeSchema,
    connectedAt: dateTimeSchema,
    connectedBy: z.string().max(320),
    refreshedAt: dateTimeSchema.nullable(),
  }).strict().nullable(),
  webhook: z.object({
    webhookId: xNumericIdSchema,
    url: z.string().max(2_048),
    valid: z.boolean(),
    createdAt: dateTimeSchema.nullable(),
    checkedAt: dateTimeSchema,
  }).strict().nullable(),
  subscriptions: z.array(z.object({
    subscriptionId: xNumericIdSchema,
    eventType: communityAdminStreamEventTypeSchema,
    webhookId: xNumericIdSchema.nullable(),
    createdAt: dateTimeSchema.nullable(),
  }).strict()),
  /** Event types without a recorded subscription. */
  missingEventTypes: z.array(communityAdminStreamEventTypeSchema),
  lastEventAt: dateTimeSchema.nullable(),
  totals: z.object({ messages: countSchema, chatters: countSchema, memberChatters: countSchema }).strict(),
  /** Most recent first, at most 20. */
  broadcasts: z.array(communityAdminStreamBroadcastSchema).max(20),
  commands: communityAdminStreamCommandsSchema,
}).strict();

/** Top-level navigation target that starts the owner connect on X. */
export const communityAdminStreamConnectResponseSchema = z.object({ authorizeUrl: z.string().url() }).strict();

export type CommunityAdminVerifyRequest = z.infer<typeof communityAdminVerifyRequestSchema>;
export type CommunityAdminIdentity = z.infer<typeof communityAdminIdentitySchema>;
export type CommunityAdminSession = z.infer<typeof communityAdminSessionSchema>;
export type CommunityAdminSessionResponse = z.infer<typeof communityAdminSessionResponseSchema>;

export type CommunityAdminPersonSummary = z.infer<typeof communityAdminPersonSummarySchema>;
export type CommunityAdminHolding = z.infer<typeof communityAdminHoldingSchema>;
export type CommunityAdminPeopleSort = z.infer<typeof communityAdminPeopleSortSchema>;
export type CommunityAdminPeopleQuery = z.infer<typeof communityAdminPeopleQuerySchema>;
export type CommunityAdminPeoplePage = z.infer<typeof communityAdminPeoplePageSchema>;
export type CommunityAdminPersonDetail = z.infer<typeof communityAdminPersonDetailSchema>;
export type CommunityAdminDisqualifyRequest = z.infer<typeof communityAdminDisqualifyRequestSchema>;
export type CommunityAdminDisqualificationResponse = z.infer<typeof communityAdminDisqualificationResponseSchema>;
export type CommunityAdminInvites = z.infer<typeof communityAdminInvitesSchema>;
export type CommunityAdminInvitesResponse = z.infer<typeof communityAdminInvitesResponseSchema>;
export type CommunityAdminFlagResponse = z.infer<typeof communityAdminFlagResponseSchema>;
export type CommunityAdminReviewFlag = z.infer<typeof communityAdminReviewFlagSchema>;
export type CommunityAdminTreeDisqualifyRequest = z.infer<typeof communityAdminTreeDisqualifyRequestSchema>;
export type CommunityAdminTreeDisqualifyResponse = z.infer<typeof communityAdminTreeDisqualifyResponseSchema>;
export type CommunityAdminProjectRemoval = z.infer<typeof communityAdminProjectRemovalSchema>;
export type CommunityAdminProjectApproval = z.infer<typeof communityAdminProjectApprovalSchema>;
export type CommunityAdminProjectStatusFilter = z.infer<typeof communityAdminProjectStatusFilterSchema>;
export type CommunityAdminProjectsQuery = z.infer<typeof communityAdminProjectsQuerySchema>;
export type CommunityAdminProject = z.infer<typeof communityAdminProjectSchema>;
export type CommunityAdminProjectsPage = z.infer<typeof communityAdminProjectsPageSchema>;
export type CommunityAdminRemoveProjectRequest = z.infer<typeof communityAdminRemoveProjectRequestSchema>;
export type CommunityAdminApproveProjectRequest = z.infer<typeof communityAdminApproveProjectRequestSchema>;
export type CommunityAdminUnapproveProjectRequest = z.infer<typeof communityAdminUnapproveProjectRequestSchema>;
export type CommunityAdminReviewSignalFlag = z.infer<typeof communityAdminReviewSignalFlagSchema>;
export type CommunityAdminHostReviewFlag = z.infer<typeof communityAdminHostReviewFlagSchema>;
export type CommunityAdminProjectReview = z.infer<typeof communityAdminProjectReviewSchema>;
export type CommunityAdminProjectCounts = z.infer<typeof communityAdminProjectCountsSchema>;
export type CommunityAdminDeclineProjectRequest = z.infer<typeof communityAdminDeclineProjectRequestSchema>;
export type CommunityAdminProjectBatchRequest = z.infer<typeof communityAdminProjectBatchRequestSchema>;
export type CommunityAdminProjectBatchResponse = z.infer<typeof communityAdminProjectBatchResponseSchema>;
export type CommunityAdminJobRun = z.infer<typeof communityAdminJobRunSchema>;
export type CommunityAdminSnapshot = z.infer<typeof communityAdminSnapshotSchema>;
export type CommunityAdminLatestJob = z.infer<typeof communityAdminLatestJobSchema>;
export type CommunityAdminOverview = z.infer<typeof communityAdminOverviewSchema>;
export type CommunityAdminJobs = z.infer<typeof communityAdminJobsSchema>;
export type CommunityAdminReferralTree = z.infer<typeof communityAdminReferralTreeSchema>;
export type CommunityAdminGraph = z.infer<typeof communityAdminGraphSchema>;
export type CommunityAdminClusters = z.infer<typeof communityAdminClustersSchema>;
export type CommunityAdminFunnel = z.infer<typeof communityAdminFunnelSchema>;
export type CommunityAdminCollection = z.infer<typeof communityAdminCollectionSchema>;
export type CommunityAdminCollections = z.infer<typeof communityAdminCollectionsSchema>;
export type CommunityAdminCreateCollectionRequest = z.infer<typeof communityAdminCreateCollectionRequestSchema>;
export type CommunityAdminRoleRule = z.infer<typeof communityAdminRoleRuleSchema>;
export type CommunityAdminRoleRules = z.infer<typeof communityAdminRoleRulesSchema>;
export type CommunityAdminCreateRoleRuleRequest = z.infer<typeof communityAdminCreateRoleRuleRequestSchema>;
export type CommunityAllowlistCriteriaV1 = z.infer<typeof communityAllowlistCriteriaV1Schema>;
export type CommunityAllowlistCriteria = z.infer<typeof communityAllowlistCriteriaSchema>;
export type CommunityAllowlistReason = z.infer<typeof communityAllowlistReasonSchema>;
export type CommunityAllowlistLeafFormat = z.infer<typeof communityAllowlistLeafFormatSchema>;
export type CommunityAdminRound = z.infer<typeof communityAdminRoundSchema>;
export type CommunityAdminRounds = z.infer<typeof communityAdminRoundsSchema>;
export type CommunityAdminCreateRoundRequest = z.infer<typeof communityAdminCreateRoundRequestSchema>;
export type CommunityAdminDecisionKind = z.infer<typeof communityAdminDecisionKindSchema>;
export type CommunityAdminDecisionRequest = z.infer<typeof communityAdminDecisionRequestSchema>;
export type CommunityAdminDecision = z.infer<typeof communityAdminDecisionSchema>;
export type CommunityAdminCandidate = z.infer<typeof communityAdminCandidateSchema>;
export type CommunityAdminEntry = z.infer<typeof communityAdminEntrySchema>;
export type CommunityAdminRoundDetail = z.infer<typeof communityAdminRoundDetailSchema>;
export type CommunityAdminFreezeRequest = z.infer<typeof communityAdminFreezeRequestSchema>;
export type CommunityAdminWinnersRequest = z.infer<typeof communityAdminWinnersRequestSchema>;
export type CommunityAdminWinnersResponse = z.infer<typeof communityAdminWinnersResponseSchema>;
export type CommunityAdminRoundExport = z.infer<typeof communityAdminRoundExportSchema>;
export type CommunityAllowlistCriteriaV2 = z.infer<typeof communityAllowlistCriteriaV2Schema>;
export type CommunityAdminStreamEventType = z.infer<typeof communityAdminStreamEventTypeSchema>;
export type CommunityAdminStreamBroadcast = z.infer<typeof communityAdminStreamBroadcastSchema>;
export type CommunityAdminStreamStatus = z.infer<typeof communityAdminStreamStatusSchema>;
export type CommunityAdminStreamCommands = z.infer<typeof communityAdminStreamCommandsSchema>;
export type CommunityAdminStreamConnectResponse = z.infer<typeof communityAdminStreamConnectResponseSchema>;
